(function () {
    'use strict';

    /**
     * Article Image Hotspots
     * Handles click & hover interactions for hotspot buttons.
     */
    class ArticleHotspots {
        constructor(container) {
            this.container = container;
            this.hotspots = Array.from(container.querySelectorAll('.js-hotspot'));
            this.popups = Array.from(container.querySelectorAll('.js-hotspot-popup'));

            if (!this.hotspots.length) return;

            this._applyPulse();
            this._positionPopups();
            this._bindEvents();
            this._bindResize();
        }

        /**
         * Apply or remove the no-pulse class based on the data-pulse attribute.
         */
        _applyPulse() {
            if (this.container.dataset.pulse === 'false') {
                this.container.classList.add('article-hotspots--no-pulse');
            } else {
                this.container.classList.remove('article-hotspots--no-pulse');
            }
        }

        /**
         * Position each popup absolutely using inline styles so they always
         * appear next to their hotspot dot, regardless of image size.
         * Horizontal positions are pixel-clamped so popups never overflow the
         * container edges (important on narrow mobile viewports).
         */
        _positionPopups() {
            const wrapper = this.container.querySelector('.article-hotspots__image-wrapper') || this.container;
            const containerW = wrapper.offsetWidth;
            const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
            const OFFSET_REM = 2.2;
            const OFFSET_PX = OFFSET_REM * remPx;
            const MARGIN_PX = 8;

            this.hotspots.forEach((hotspot) => {
                const index = hotspot.dataset.index;
                const popup = this.container.querySelector(`.js-hotspot-popup[data-index="${index}"]`);
                if (!popup) return;

                const xPct = parseFloat(hotspot.style.left);  // e.g. 30
                const yPct = parseFloat(hotspot.style.top);   // e.g. 40

                const goLeft = xPct > 55;
                const goAbove = yPct > 55;

                // Reset any previously applied position properties
                popup.style.top = '';
                popup.style.bottom = '';
                popup.style.left = '';
                popup.style.right = '';

                // Vertical: keep percentage-based
                if (!goAbove) {
                    popup.style.top = `calc(${yPct}% + ${OFFSET_REM}rem)`;
                } else {
                    popup.style.bottom = `calc(${100 - yPct}% + ${OFFSET_REM}rem)`;
                }

                // Horizontal: pixel-based with clamping to stay within container
                const popupW = popup.offsetWidth;
                if (!goLeft) {
                    let left = (xPct / 100) * containerW + OFFSET_PX;
                    if (containerW && popupW) {
                        left = Math.max(MARGIN_PX, Math.min(left, containerW - popupW - MARGIN_PX));
                    }
                    popup.style.left = left + 'px';
                } else {
                    let right = ((100 - xPct) / 100) * containerW + OFFSET_PX;
                    if (containerW && popupW) {
                        right = Math.max(MARGIN_PX, Math.min(right, containerW - popupW - MARGIN_PX));
                    }
                    popup.style.right = right + 'px';
                }
            });
        }

        _bindResize() {
            let raf;
            const onResize = () => {
                cancelAnimationFrame(raf);
                raf = requestAnimationFrame(() => this._positionPopups());
            };
            window.addEventListener('resize', onResize, { passive: true });
        }

        _bindEvents() {
            this.hotspots.forEach((hotspot) => {
                // --- Touch devices ---
                // touchend + preventDefault() fully suppresses the synthetic click
                // event the browser would generate, preventing any navigation.
                // On the second tap (popup already active) we do NOT preventDefault,
                // so the synthetic click fires and the <a> navigates normally.
                hotspot.addEventListener('touchend', (e) => {
                    if (!hotspot.classList.contains('is-active')) {
                        e.preventDefault(); // kills synthetic click → no navigation, no doc-click-close
                        this._closeAll();
                        this._open(hotspot);
                    }
                    // already active → synthetic click fires → navigates to product
                });

                // --- Desktop mouse click ---
                // On touch: only reached when touchend did NOT call preventDefault
                // (i.e. popup was already active) → just navigate.
                // On desktop: if somehow not active yet (edge case), open popup.
                hotspot.addEventListener('click', (e) => {
                    if (!hotspot.classList.contains('is-active')) {
                        e.preventDefault();
                        e.stopPropagation();
                        this._closeAll();
                        this._open(hotspot);
                    }
                    // already active → navigate
                });

                // Hover (desktop only) — open on enter, close on leave
                hotspot.addEventListener('mouseenter', () => {
                    this._closeAll();
                    this._open(hotspot);
                });

                hotspot.addEventListener('mouseleave', (e) => {
                    const popup = this._getPopup(hotspot);
                    if (popup && e.relatedTarget && popup.contains(e.relatedTarget)) return;
                    this._close(hotspot);
                });
            });

            // Keep popup open while hovering it (desktop)
            this.popups.forEach((popup) => {
                popup.addEventListener('mouseleave', (e) => {
                    const hotspot = this._getHotspotForPopup(popup);
                    if (hotspot && e.relatedTarget && hotspot.contains(e.relatedTarget)) return;
                    this._close(hotspot);
                });
            });

            // Tap/click outside closes all
            document.addEventListener('click', () => this._closeAll());
        }

        _open(hotspot) {
            const popup = this._getPopup(hotspot);
            hotspot.classList.add('is-active');
            if (popup) {
                popup.classList.add('is-active');
                popup.querySelector('a')?.setAttribute('tabindex', '0');
            }
        }

        _close(hotspot) {
            if (!hotspot) return;
            const popup = this._getPopup(hotspot);
            hotspot.classList.remove('is-active');
            if (popup) {
                popup.classList.remove('is-active');
                popup.querySelector('a')?.setAttribute('tabindex', '-1');
            }
        }

        _closeAll() {
            this.hotspots.forEach((h) => this._close(h));
        }

        _getPopup(hotspot) {
            return this.container.querySelector(
                `.js-hotspot-popup[data-index="${hotspot.dataset.index}"]`
            );
        }

        _getHotspotForPopup(popup) {
            return this.container.querySelector(
                `.js-hotspot[data-index="${popup.dataset.index}"]`
            );
        }
    }

    /* -------------------------------------------------------
       Initialisation
    ------------------------------------------------------- */
    function init(scope) {
        const root = scope || document;
        root
            .querySelectorAll('.article-hotspots[data-section-id]')
            .forEach((el) => {
                if (!el._hotspots) {
                    el._hotspots = new ArticleHotspots(el);
                }
            });
    }

    document.addEventListener('DOMContentLoaded', () => {
        init();

        // Shopify theme editor live reload
        document.addEventListener('shopify:section:load', (e) => {
            init(e.target);
        });

        document.addEventListener('shopify:block:select', (e) => {
            const container = e.target.closest('.article-hotspots[data-section-id]');
            if (!container) return;
            const hotspot = e.target.classList.contains('js-hotspot')
                ? e.target
                : e.target.querySelector('.js-hotspot');
            if (hotspot && container._hotspots) {
                container._hotspots._closeAll();
                container._hotspots._open(hotspot);
            }
        });

        document.addEventListener('shopify:block:deselect', (e) => {
            const container = e.target.closest('.article-hotspots[data-section-id]');
            if (container?._hotspots) container._hotspots._closeAll();
        });
    });
})();
